import { makeAutoObservable } from 'mobx';
import { authorizedRequest } from '../utils/authorization';
import { Currency } from '../types';
import { Company } from '../pages/Connection/components/AboutCompany';
import Auth from './Auth';

type UserType = {
  username: string;
  email: string;
  full_name: string;
  roles: any;
  onboarding: boolean;
  company: {
    groups: string[];
    id: string;
    name: string;
  };
};

type Me = {
  id: string;
  email: string | null;
  status: string | null;
  role: string | null;
  first_name: string | null;
  last_name: string | null;
  last_login: string | null;
  created: string | null;
  modified: string | null;
  company: Company;
};

type OnboardingStepsType = {
  appGameInfo: boolean;
  basicCompanyInfo: boolean;
  companySignatory: boolean;
  contactPerson: boolean;
  corporateDocuments: boolean;
  UBOsDirectors: boolean;
};

class User {
  constructor() {
    makeAutoObservable(this);
  }

  user: UserType | null = null;
  me: Me | null = null;
  company = {};
  onboardingSteps: OnboardingStepsType | null = null;
  isLoadingUser: boolean = false;
  isLoadingMe: boolean = false;
  isLoadingOnboarding: boolean = false;

  get getDefaultCurrency() {
    if (this.me?.company?.settings?.operating_currency) {
      return {
        label: this.me?.company?.settings?.operating_currency,
        value: this.me?.company?.settings?.operating_currency,
      };
    }
    return null;
  }

  getUserInfo = async () => {
    // Проверяем авторизацию перед запросом
    if (!Auth.isAuthenticated || !Auth.token) {
      console.log('User not authenticated, skipping user info request');
      return;
    }

    // Проверяем, что данные еще не загружены и не загружаются
    if (this.user || this.isLoadingUser) {
      return;
    }

    this.isLoadingUser = true;
    try {
      this.user = await authorizedRequest({ url: '/ui/api/v1/current_user' });
    } catch (error: any) {
      console.error('Failed to get user info:', error);

      // Если 401 - пользователь не авторизован, очищаем данные
      if (error?.response?.status === 401) {
        Auth.clearAuth();
        return;
      }
    } finally {
      this.isLoadingUser = false;
    }
  };

  getMe = async () => {
    // Приоритет: используем данные из Auth store, если они есть
    if (Auth.user && !this.me) {
      this.me = Auth.user as any; // Временное преобразование типов
      return;
    }

    // Проверяем авторизацию перед запросом
    if (!Auth.isAuthenticated || !Auth.token) {
      console.log('User not authenticated, skipping me request');
      return;
    }

    // Если данные уже есть или загружаются, не делаем запрос
    if (this.me || this.isLoadingMe) {
      return;
    }

    this.isLoadingMe = true;
    try {
      this.me = await authorizedRequest({
        url: '/v1/auth/me',
        service: 'fca',
      });
    } catch (error: any) {
      console.error('Failed to get me:', error);

      // Если 401 - пользователь не авторизован, очищаем данные
      if (error?.response?.status === 401) {
        Auth.clearAuth();
        return;
      }
    } finally {
      this.isLoadingMe = false;
    }
  };

  getOnboarding = async (force = false) => {
    // Проверяем авторизацию перед запросом
    if (!Auth.isAuthenticated || !Auth.token) {
      console.log('User not authenticated, skipping onboarding request');
      return;
    }

    // Проверяем, что данные еще не загружены и не загружаются
    // Если force=true, то игнорируем проверку на уже загруженные данные
    if (!force && (this.onboardingSteps || this.isLoadingOnboarding)) {
      return;
    }

    // Если уже идет загрузка, не запускаем повторно
    if (this.isLoadingOnboarding) {
      return;
    }

    this.isLoadingOnboarding = true;
    try {
      // ИСПРАВЛЕНО: используем правильный эндпоинт БЕЗ companyId
      // /v1/onboarding/ возвращает OnboardingStatus со статусами завершения
      const response = await authorizedRequest({
        url: `/v1/onboarding/`,
        service: 'sca',
      });

      // Безопасно присваиваем результат
      this.onboardingSteps = response || null;
    } catch (error: any) {
      console.error('Failed to get onboarding:', error);

      // Если 401 - пользователь не авторизован, очищаем данные
      if (error?.response?.status === 401) {
        Auth.clearAuth();
        return;
      }

      // Если 404 - онбординг данные не найдены, это означает что нужно показывать онбординг
      if (error?.response?.status === 404) {
        console.log('Onboarding data not found (404) - user needs onboarding');
        this.onboardingSteps = null;
        return;
      }

      // Для других ошибок просто логируем, но не бросаем исключение
      this.onboardingSteps = null;
    } finally {
      this.isLoadingOnboarding = false;
    }
  };

  // Метод для сброса состояния при логауте
  reset = () => {
    this.user = null;
    this.me = null;
    this.company = {};
    this.onboardingSteps = null;
    this.isLoadingUser = false;
    this.isLoadingMe = false;
    this.isLoadingOnboarding = false;
  };
}

const userStore = new User();
export default userStore;
